import { Module } from 'vuex'

import { GetOfficialProblemCategoryUseCase } from '@/application/problem/getOfficialProblemCategoryUseCase'
import { GetProblemAttachmentUseCase } from '@/application/problem/getProblemAttachmentUseCase'
import { GetProblemStatisticsUseCase } from '@/application/problem/getProblemStatisticsUseCase'
import { OfficialProblemCategory } from '@/domain/entities/problem/officialProblemCategory'
import { ProblemAttachment } from '@/domain/entities/problem/problemAttachment'
import { ProblemStatistics } from '@/domain/entities/problem/problemStatistics'
import { ProblemRepository } from '@/domain/repositories/problem/ProblemRepository'

interface ProblemStatisticsState {
  problemAttachment: ProblemAttachment
  problemStatistics: ProblemStatistics
  officialProblemCategoryList: OfficialProblemCategory[]
  isLoading: boolean
  error: string | null
}

export const problemStatisticsModule = (problemRepository: ProblemRepository): Module<ProblemStatisticsState, any> => {
  const getProblemAttachmentUseCase = new GetProblemAttachmentUseCase(problemRepository)
  const getProblemStatisticsUseCase = new GetProblemStatisticsUseCase(problemRepository)
  const getOfficialProblemCategoryUseCase = new GetOfficialProblemCategoryUseCase(problemRepository)

  return {
    namespaced: true,
    state: {
      problemAttachment: {
        problemId: 'ITP1_1_A',
        recommendations: [],
        bookmarks: [],
        problemCategories: [],
        algorithmCategories: [],
        tags: []
      },
      problemStatistics: {
        problemId: 'ITP1_1_A',
        statusStatistics: {
          status: [],
          value: []
        },
        languageStatistics: {
          language: [],
          value: []
        }
      },
      officialProblemCategoryList: [],
      isLoading: false,
      error: null
    },
    mutations: {
      setProblemAttachment(state, problemAttachment: ProblemAttachment) {
        state.problemAttachment = problemAttachment
      },
      setProblemStatistics(state, problemStatistics: ProblemStatistics) {
        state.problemStatistics = problemStatistics
      },
      setOfficialProblemCategoryList(state, officialProblemCategoryList: OfficialProblemCategory[]) {
        state.officialProblemCategoryList = officialProblemCategoryList
      },
      setIsLoading(state, isLoading: boolean) {
        state.isLoading = isLoading
      },
      setError(state, error: string | null) {
        state.error = error
      }
    },
    actions: {
      async getProblemStatistics({ commit }, { problemId }: { problemId: string }) {
        commit('setIsLoading', true)
        commit('setError', null)
        try {
          const attachment = await getProblemAttachmentUseCase.getProblemAttachment(problemId)
          commit('setProblemAttachment', attachment)

          const statistics = await getProblemStatisticsUseCase.getProblemStatistics(problemId)
          commit('setProblemStatistics', statistics)
        } catch (e) {
          commit('setError', e)
        } finally {
          commit('setIsLoading', false)
        }
      },
      async getOfficialProblemCategory({ commit }) {
        commit('setIsLoading', true)
        commit('setError', null)
        try {
          const res = await getOfficialProblemCategoryUseCase.getOfficialProblemCategory()
          commit('setOfficialProblemCategoryList', res)
        } catch (e) {
          commit('setError', e)
        } finally {
          commit('setIsLoading', false)
        }
      }
    },
    getters: {
      problemAttachment: state => state.problemAttachment,
      problemStatistics: state => state.problemStatistics,
      officialProblemCategoryList: state => state.officialProblemCategoryList,
      isLoading: state => state.isLoading,
      error: state => state.error
    }
  }
}
